import { create } from 'zustand';
import { clearQualificationDraft } from './qualificationDraft';
import { persist } from 'zustand/middleware';
import { Patient } from '@bloom/shared';

interface DtpAuthState {
  token: string | null;
  patient: Patient | null;
  physicianId: string | null;
  isAuthenticated: boolean;
  // Temporary signup token (after OTP verified, before patient created)
  signupToken: string | null;
  phone: string | null;
  /** True when a clinic or staff is viewing this portal, not the patient. */
  impersonated: boolean;
}

interface DtpAuthActions {
  setAuth: (token: string, patient: Patient, physicianId: string, impersonated?: boolean) => void;
  setSignupPending: (signupToken: string, physicianId: string, phone: string) => void;
  logout: () => void;
}

type DtpAuthStore = DtpAuthState & DtpAuthActions;

export const useDtpAuthStore = create<DtpAuthStore>()(
  persist(
    (set) => ({
      token: null,
      patient: null,
      physicianId: null,
      isAuthenticated: false,
      signupToken: null,
      phone: null,
      impersonated: false,

      setAuth: (token, patient, physicianId, impersonated = false) => {
        localStorage.setItem('dtp-token', token);
        set({
          token,
          patient,
          physicianId,
          isAuthenticated: true,
          signupToken: null,
          phone: null,
          impersonated
        });
      },

      setSignupPending: (signupToken, physicianId, phone) => {
        // A leftover patient token from an earlier visit would still ride along on
        // every request (dtpApi reads it from localStorage) and its 401 would log
        // this new signup out mid-checkout.
        localStorage.removeItem('dtp-token');
        set({
          signupToken,
          physicianId,
          phone,
          isAuthenticated: false,
          token: null,
          patient: null
        });
      },

      logout: () => {
        clearQualificationDraft();
        localStorage.removeItem('dtp-token');
        set({
          token: null,
          patient: null,
          physicianId: null,
          isAuthenticated: false,
          signupToken: null,
          phone: null,
          impersonated: false
        });
      }
    }),
    {
      name: 'dtp-auth',
      partialize: (state) => ({
        token: state.token,
        patient: state.patient,
        physicianId: state.physicianId,
        isAuthenticated: state.isAuthenticated,
        // Persisted with the token it describes: a reload must not silently
        // turn a staff view into what looks like the patient's own session.
        impersonated: state.impersonated
      })
    }
  )
);
